import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Code as CodeIcon,
  PlayArrow as TestIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

const CustomWebhookDetail = () => {
  const { webhookId } = useParams();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { openSuccessModal, openErrorModal } = useModal();

  const [webhook, setWebhook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [fieldMappings, setFieldMappings] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [editingMapping, setEditingMapping] = useState(null);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPayload, setTestPayload] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [logs, setLogs] = useState([]);

  const [mappingForm, setMappingForm] = useState({
    source_field: '',
    target_field: '',
    field_type: 'text',
    field_category: 'custom',
    is_required: false,
    default_value: '',
    order: 0,
  });

  useEffect(() => {
    fetchWebhookDetails();
    fetchFieldMappings();
    fetchAvailableFields();
  }, [webhookId]);

  useEffect(() => {
    if (tabValue === 2) {
      fetchWebhookLogs();
    }
  }, [tabValue]);

  const fetchWebhookDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/lms/integrations/custom-webhooks/${webhookId}/`);
      setWebhook(response.data.webhook);
    } catch (error) {
      console.error('Error fetching webhook:', error);
      openErrorModal('Failed to load webhook details');
      navigate('/settings/custom-webhooks');
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldMappings = async () => {
    try {
      const response = await api.get(`/lms/integrations/custom-webhooks/${webhookId}/field-mappings/`);
      setFieldMappings(response.data.field_mappings || []);
    } catch (error) {
      console.error('Error fetching field mappings:', error);
    }
  };

  const fetchAvailableFields = async () => {
    try {
      const response = await api.get('/lms/integrations/available-lead-fields/');
      setAvailableFields(response.data.fields || []);
    } catch (error) {
      console.error('Error fetching available fields:', error);
    }
  };

  const fetchWebhookLogs = async () => {
    try {
      const response = await api.get(`/lms/integrations/custom-webhooks/${webhookId}/logs/`);
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching webhook logs:', error);
    }
  };

  const handleCopyWebhookUrl = () => {
    if (webhook && webhook.webhook_url) {
      navigator.clipboard.writeText(webhook.webhook_url);
      openSuccessModal('Webhook URL copied to clipboard');
    }
  };

  const handleCopyToken = () => {
    if (webhook && webhook.webhook_token) {
      navigator.clipboard.writeText(webhook.webhook_token);
      openSuccessModal('Webhook token copied to clipboard');
    }
  };

  const handleAddMapping = () => {
    setEditingMapping(null);
    setMappingForm({
      source_field: '',
      target_field: '',
      field_type: 'text',
      field_category: 'custom',
      is_required: false,
      default_value: '',
      order: fieldMappings.length,
    });
    setMappingDialogOpen(true);
  };

  const handleEditMapping = (mapping) => {
    setEditingMapping(mapping);
    setMappingForm({
      source_field: mapping.source_field,
      target_field: mapping.target_field,
      field_type: mapping.field_type,
      field_category: mapping.field_category,
      is_required: mapping.is_required,
      default_value: mapping.default_value || '',
      order: mapping.order,
    });
    setMappingDialogOpen(true);
  };

  const handleSaveMapping = async () => {
    if (!mappingForm.source_field || !mappingForm.target_field) {
      openErrorModal('Source and target fields are required');
      return;
    }

    try {
      showLoader('Saving field mapping...');

      if (editingMapping) {
        // Update existing mapping
        await api.put(
          `/lms/integrations/custom-webhooks/${webhookId}/field-mappings/${editingMapping.id}/`,
          mappingForm
        );
      } else {
        // Create new mapping
        await api.post(
          `/lms/integrations/custom-webhooks/${webhookId}/field-mappings/`,
          mappingForm
        );
      }

      openSuccessModal('Field mapping saved successfully');
      setMappingDialogOpen(false);
      fetchFieldMappings();
    } catch (error) {
      openErrorModal(error.response?.data?.error || 'Failed to save field mapping');
    } finally {
      hideLoader();
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this field mapping?')) {
      return;
    }

    try {
      showLoader('Deleting field mapping...');
      await api.delete(`/lms/integrations/custom-webhooks/${webhookId}/field-mappings/${mappingId}/`);
      openSuccessModal('Field mapping deleted successfully');
      fetchFieldMappings();
    } catch (error) {
      openErrorModal(error.response?.data?.error || 'Failed to delete field mapping');
    } finally {
      hideLoader();
    }
  };

  const handleTestWebhook = async () => {
    try {
      // Parse test payload
      let payload;
      try {
        payload = JSON.parse(testPayload);
      } catch (e) {
        openErrorModal('Invalid JSON payload');
        return;
      }

      showLoader('Testing webhook...');
      const response = await api.post(
        `/lms/integrations/custom-webhooks/${webhookId}/test/`,
        { payload }
      );

      setTestResult(response.data.test_result);
      openSuccessModal('Webhook test completed');
    } catch (error) {
      setTestResult({
        success: false,
        errors: [error.response?.data?.error || 'Test failed'],
      });
      openErrorModal('Webhook test failed');
    } finally {
      hideLoader();
    }
  };

  const handleImportFromSample = () => {
    try {
      const payload = JSON.parse(testPayload);
      const detectedMappings = [];
      let order = fieldMappings.length;

      // Auto-detect field mappings from payload
      Object.keys(payload).forEach((key) => {
        const lowerKey = key.toLowerCase();
        let targetField = '';
        let fieldType = 'text';

        // Auto-map common fields
        if (lowerKey.includes('name') && !lowerKey.includes('last')) {
          targetField = 'first_name';
        } else if (lowerKey.includes('lastname') || lowerKey.includes('last_name')) {
          targetField = 'last_name';
        } else if (lowerKey.includes('email')) {
          targetField = 'email';
          fieldType = 'email';
        } else if (lowerKey.includes('phone') || lowerKey.includes('mobile')) {
          targetField = 'mobile';
          fieldType = 'phone';
        } else if (lowerKey.includes('budget')) {
          if (lowerKey.includes('min')) {
            targetField = 'budget_min';
          } else if (lowerKey.includes('max')) {
            targetField = 'budget_max';
          }
          fieldType = 'number';
        }

        if (targetField) {
          detectedMappings.push({
            source_field: key,
            target_field: targetField,
            field_type: fieldType,
            field_category: 'basic',
            is_required: false,
            default_value: '',
            order: order++,
          });
        }
      });

      if (detectedMappings.length > 0) {
        // Save all detected mappings
        saveBulkMappings(detectedMappings);
      } else {
        openErrorModal('No common fields detected. Please map manually.');
      }
    } catch (e) {
      openErrorModal('Invalid JSON payload');
    }
  };

  const saveBulkMappings = async (mappings) => {
    try {
      showLoader('Importing field mappings...');
      await api.post(
        `/lms/integrations/custom-webhooks/${webhookId}/field-mappings/bulk/`,
        {
          field_mappings: mappings,
          replace_all: false,
        }
      );
      openSuccessModal(`Imported ${mappings.length} field mappings`);
      setTestDialogOpen(false);
      fetchFieldMappings();
    } catch (error) {
      openErrorModal('Failed to import field mappings');
    } finally {
      hideLoader();
    }
  };

  const generateSampleCurl = () => {
    if (!webhook) return '';

    const samplePayload = {
      first_name: 'John',
      last_name: 'Doe',
      email: 'john.doe@example.com',
      mobile: '9876543210',
      requirement: 'Looking for 3BHK apartment',
    };

    return `curl -X POST \\
  "${webhook.webhook_url}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(samplePayload, null, 2)}'`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!webhook) {
    return null;
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <IconButton onClick={() => navigate('/settings/custom-webhooks')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight="bold">
              {webhook.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {webhook.description || 'No description provided'}
            </Typography>
          </Box>
          <Chip
            icon={webhook.is_active ? <CheckCircleIcon /> : <ErrorIcon />}
            label={webhook.is_active ? 'Active' : 'Inactive'}
            color={webhook.is_active ? 'success' : 'default'}
          />
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Configuration" />
          <Tab label="Field Mappings" />
          <Tab label="Activity Log" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Box>
          {/* Webhook URL */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Webhook URL
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Share this URL with your external service to send webhook requests
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  value={webhook.webhook_url}
                  InputProps={{ readOnly: true }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  startIcon={<CopyIcon />}
                  onClick={handleCopyWebhookUrl}
                  size="small"
                >
                  Copy
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Total Requests
                  </Typography>
                  <Typography variant="h5">{webhook.total_requests || 0}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Successful
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {webhook.successful_requests || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Failed
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {webhook.failed_requests || 0}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="text.secondary">
                    Success Rate
                  </Typography>
                  <Typography variant="h5">{webhook.success_rate || 0}%</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Sample cURL Command */}
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                Sample cURL Command
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Use this command to test your webhook
              </Typography>
              <Box
                sx={{
                  bgcolor: '#f5f5f5',
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  position: 'relative',
                }}
              >
                <pre style={{ margin: 0, fontSize: '0.7rem', overflow: 'auto' }}>
                  {generateSampleCurl()}
                </pre>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {tabValue === 1 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Field Mappings ({fieldMappings.length})
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<CodeIcon />}
                onClick={() => setTestDialogOpen(true)}
                sx={{ mr: 1 }}
              >
                Import from Sample
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddMapping}
              >
                Add Mapping
              </Button>
            </Box>
          </Box>

          {fieldMappings.length === 0 ? (
            <Alert severity="info">
              No field mappings configured yet. Add mappings to transform incoming webhook data into Salesgro leads.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Source Field</strong></TableCell>
                    <TableCell><strong>Target Field</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Required</strong></TableCell>
                    <TableCell><strong>Default</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fieldMappings.map((mapping) => (
                    <TableRow key={mapping.id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {mapping.source_field}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{mapping.target_field}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={mapping.field_type} size="small" />
                      </TableCell>
                      <TableCell>
                        {mapping.is_required ? (
                          <CheckCircleIcon fontSize="small" color="success" />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {mapping.default_value || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditMapping(mapping)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteMapping(mapping.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {tabValue === 2 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 2 }}>
            Recent Activity ({logs.length})
          </Typography>
          {logs.length === 0 ? (
            <Alert severity="info">No webhook requests received yet</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Timestamp</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Lead ID</strong></TableCell>
                    <TableCell><strong>Error</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(log.created_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          color={log.status === 'success' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {log.lead_id ? (
                          <Typography variant="body2">{log.lead_id}</Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {log.error_message ? (
                          <Typography variant="caption" color="error">
                            {log.error_message}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Field Mapping Dialog */}
      <Dialog
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMapping ? 'Edit Field Mapping' : 'Add Field Mapping'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              label="Source Field"
              fullWidth
              required
              value={mappingForm.source_field}
              onChange={(e) =>
                setMappingForm({ ...mappingForm, source_field: e.target.value })
              }
              sx={{ mb: 1.5 }}
              size="small"
              helperText="Field name in incoming payload (e.g., 'customer_name', 'email_address')"
            />

            <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
              <InputLabel>Target Field *</InputLabel>
              <Select
                value={mappingForm.target_field}
                onChange={(e) =>
                  setMappingForm({ ...mappingForm, target_field: e.target.value })
                }
                label="Target Field *"
              >
                {availableFields.map((field) => (
                  <MenuItem key={field.name} value={field.name}>
                    {field.label} ({field.name})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 1.5 }} size="small">
              <InputLabel>Field Type</InputLabel>
              <Select
                value={mappingForm.field_type}
                onChange={(e) =>
                  setMappingForm({ ...mappingForm, field_type: e.target.value })
                }
                label="Field Type"
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="phone">Phone</MenuItem>
                <MenuItem value="number">Number</MenuItem>
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="boolean">Boolean</MenuItem>
                <MenuItem value="json">JSON</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Default Value"
              fullWidth
              value={mappingForm.default_value}
              onChange={(e) =>
                setMappingForm({ ...mappingForm, default_value: e.target.value })
              }
              sx={{ mb: 1.5 }}
              size="small"
              helperText="Value to use if field is missing in payload"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={mappingForm.is_required}
                  onChange={(e) =>
                    setMappingForm({ ...mappingForm, is_required: e.target.checked })
                  }
                />
              }
              label="Required field"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMappingDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveMapping} variant="contained" color="primary">
            Save Mapping
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Webhook Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Test Webhook & Import Fields</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Paste a sample JSON payload to test the webhook and automatically detect field mappings
            </Typography>
            <TextField
              label="Sample JSON Payload"
              fullWidth
              multiline
              rows={8}
              value={testPayload}
              onChange={(e) => setTestPayload(e.target.value)}
              placeholder='{\n  "first_name": "John",\n  "last_name": "Doe",\n  "email": "john@example.com"\n}'
              sx={{ mb: 1.5 }}
              size="small"
            />

            {testResult && (
              <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 1.5 }}>
                {testResult.success ? (
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      Test Successful!
                    </Typography>
                    <Typography variant="caption">
                      Mapped data ready for lead creation
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      Test Failed
                    </Typography>
                    {testResult.errors?.map((error, idx) => (
                      <Typography key={idx} variant="caption" display="block">
                        • {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
          <Button onClick={handleImportFromSample} variant="outlined">
            Auto-Detect & Import
          </Button>
          <Button
            onClick={handleTestWebhook}
            variant="contained"
            startIcon={<TestIcon />}
          >
            Test Webhook
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomWebhookDetail;
